/**
 * Equipe e acessos — §5.7 da spec 04, reorganizada POR TIME no H129 (spec 24, 01/11).
 *
 * O owner: *"não estou vendo aonde que eu vinculo a equipe para cada supervisor… essa tela ainda parece ser muito
 * fraca"*. Depois da pesquisa (`docs/mercado/equipe-e-supervisor.md`) ele disse *"concordo"* às cinco recomendações.
 * A tela agora tem três abas:
 *
 * - **Times** (primeiro): um cartão por fila — responsáveis, membros com presença e carga × limite, a IA como membro,
 *   os números de agora e as ações. Com uma fila só, ela já abre nela, inteira (`equipe-times.tsx`).
 * - **Pessoas**: lista com filtros (time, presença, jornada) e o registro de cada pessoa na gaveta
 *   (`equipe-pessoas.tsx`). `?pessoa=<id>` abre o registro — é por onde o Início chega.
 * - **Perfis**: só MOSTRA o que cada papel pode, com o link para administrar no Cert4All. Matriz editável não existe
 *   aqui: os papéis vêm de lá (invariante 1 da spec 15).
 *
 * **Convidar** e os convites pendentes viraram botões do cabeçalho; a ação primária é **Novo time**.
 *
 * ## O que esta tela NÃO tem, e continua sendo a decisão principal
 *
 * **Nenhum campo de senha, e-mail ou MFA.** Identidade é do Cert4All. Por isso o botão é "Convidar", e não
 * "Criar usuário". E a chave solta "Ver só as filas dela" SAIU (ADR-0022): o alcance do supervisor nasce dos times de
 * que ele é responsável; quem já a tinha ligada vê, no registro, "ajuste antigo" com a opção de desligar.
 */

import { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { Botao, EstadoDaFalha, falhaDe, useSessao } from "@hugi/ui";
import { CabecalhoDaTela, Carregando, Dialogo, Gaveta, GradeDeCartoes, PaginaInteira, Selo, Vazio } from "@hugi/ui/kit";
import { ExternalLink, Mail, Users } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useEquipe,
  useTimes,
  convidarParaEquipe,
  operarConviteDaEquipe,
  type Equipe as DadosDaEquipe,
} from "./dados.js";
import { AbaTimes, DialogoDoNovoTime } from "./equipe-times.js";
import { AbaPessoas, RegistroDaPessoa } from "./equipe-pessoas.js";
import "./equipe.css";

// §5.7(a): a tela diz de onde vem a identidade, em uma linha, para o admin não procurar a senha aqui.
const DICA = "Senha e papel são do Cert4All. Aqui: os times, quem supervisiona cada um, presença, jornada e limite.";

/** Onde se administra papel e permissão: no Cert4All, na administração da empresa (`/company-admin` de lá). */
const URL_DOS_PAPEIS = "https://cert4all.com.br/company-admin";

type Aba = "times" | "pessoas" | "perfis";

export function Equipe() {
  const sessao = useSessao();
  const q = useEquipe();
  const veOTime = sessao.permissoes.includes("read_team_conversations") || sessao.permissoes.includes("manage_team");
  const times = useTimes(veOTime);
  const busca = useSearch();
  const [caminho, navegar] = useLocation();
  const parametros = new URLSearchParams(busca);
  const pedida = parametros.get("pessoa");
  const abaPedida = parametros.get("aba") as Aba | null;
  // Times é a primeira — para quem vê o time. Quem não vê (atendente) abre em Pessoas, onde está ele mesmo.
  const aba: Aba = pedida ? "pessoas" : abaPedida && ["times", "pessoas", "perfis"].includes(abaPedida) ? abaPedida : veOTime ? "times" : "pessoas";
  const irPara = (mudar: (q: URLSearchParams) => void) => {
    const nova = new URLSearchParams(busca);
    mudar(nova);
    const texto = nova.toString();
    navegar(texto ? `${caminho}?${texto}` : caminho, { replace: true });
  };
  const [convidando, setConvidando] = useState(false);
  const [vendoConvites, setVendoConvites] = useState(false);
  const [criando, setCriando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  if (q.isPending || q.error) {
    return (
      <PaginaInteira rotulo="Equipe e acessos">
        <CabecalhoDaTela icone={<Users size={18} />} titulo="Equipe e acessos" dica={DICA} />
        {q.isPending ? (
          <Carregando />
        ) : (
          <EstadoDaFalha
            falha={falhaDe(q.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
            aoRepetir={() => void q.refetch()}
          />
        )}
      </PaginaInteira>
    );
  }

  const d = q.data!;
  const podeCriarTime = sessao.permissoes.includes("manage_catalog");
  const listaDeTimes = times.data?.times ?? [];
  const aberta = pedida ? (d.itens.find((a) => a.atendenteId === pedida) ?? null) : null;

  /*
    Convidar — o fluxo é do Cert4All. Quem decide se o botão vale é o SERVIDOR (`podeConvidar`). A primária da tela é
    "Novo time" (uma por tela); sem permissão de criar time, Convidar vira a primária.
  */
  const convidar = d.podeConvidar ? (
    <Botao tipo={podeCriarTime ? "secundario" : "primario"} onClick={() => setConvidando(true)}>
      Convidar
    </Botao>
  ) : (
    <Botao
      tipo="secundario"
      desabilitado
      motivoDeDesabilitar={d.porQuePerfilNaoSalva || "Convidar é um fluxo do Cert4All, e só quem administra a empresa por lá pode usá-lo."}
    >
      Convidar
    </Botao>
  );

  const abas = [
    ...(veOTime
      ? [{
          id: "times",
          rotulo: "Times",
          contador: listaDeTimes.length,
          conteudo: () =>
            times.isPending ? (
              <Carregando />
            ) : times.error ? (
              <EstadoDaFalha falha={falhaDe(times.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }} aoRepetir={() => void times.refetch()} />
            ) : (
              <AbaTimes
                times={listaDeTimes}
                pessoas={d.itens}
                motivos={times.data?.motivosDePausa ?? d.motivosDePausa ?? []}
                aoCriar={podeCriarTime ? () => setCriando(true) : undefined}
              />
            ),
        }]
      : []),
    {
      id: "pessoas",
      rotulo: "Pessoas",
      contador: d.itens.length,
      conteudo: () => (
        <AbaPessoas
          dados={d}
          times={listaDeTimes}
          pedida={pedida}
          aoAbrir={(id) =>
            irPara((x) => {
              x.set("aba", "pessoas");
              if (id) x.set("pessoa", id);
              else x.delete("pessoa");
            })
          }
        />
      ),
    },
    { id: "perfis", rotulo: "Perfis", contador: d.perfis.length, conteudo: () => <Perfis dados={d} /> },
  ];

  return (
    <PaginaInteira rotulo="Equipe e acessos">
      <CabecalhoDaTela
        icone={<Users size={18} />}
        titulo="Equipe e acessos"
        dica={DICA}
        {...(aviso ? { recorte: <span role="status">{aviso}</span> } : {})}
        secundarias={
          <>
            {d.convitesPendentes.length > 0 ? (
              <button type="button" className="v2-botao" onClick={() => setVendoConvites(true)}>
                <Mail size={14} aria-hidden="true" />
                Convites
                <span className="v2-contador">{d.convitesPendentes.length}</span>
              </button>
            ) : null}
            {podeCriarTime ? convidar : null}
          </>
        }
        primaria={podeCriarTime ? <Botao onClick={() => setCriando(true)}>Novo time</Botao> : convidar}
      />

      <GradeDeCartoes
        rotulo="Equipe e acessos"
        cartoes={[
          {
            id: "equipe",
            titulo: "Equipe",
            abas,
            aba,
            aoTrocarAba: (id) =>
              irPara((x) => {
                x.set("aba", id);
                x.delete("pessoa");
              }),
          },
        ]}
      />

      <Dialogo aberto={convidando} aoFechar={() => setConvidando(false)} titulo="Convidar para a equipe">
        <FormDoConvite
          dados={d}
          aoEnviar={(texto) => {
            setAviso(texto);
            setConvidando(false);
          }}
          aoCancelar={() => setConvidando(false)}
        />
      </Dialogo>

      <Dialogo aberto={vendoConvites} aoFechar={() => setVendoConvites(false)} titulo="Convites pendentes" largura="estreita">
        <Convites dados={d} />
      </Dialogo>

      <DialogoDoNovoTime aberto={criando} aoFechar={() => setCriando(false)} pessoas={d.itens} />

      <Gaveta
        aberta={aberta !== null}
        aoFechar={() => irPara((x) => x.delete("pessoa"))}
        titulo={aberta ? (aberta.nome ?? "Sem nome ainda") : "Pessoa"}
        largura="larga"
      >
        {aberta ? <RegistroDaPessoa key={aberta.atendenteId} atendente={aberta} dados={d} times={listaDeTimes} /> : null}
      </Gaveta>
    </PaginaInteira>
  );
}

/**
 * O convite. O perfil vai NO CONVITE, e o padrão é Atendente: se alguém não escolher, a pessoa chega com o mínimo,
 * e não com o máximo. A lista de perfis vem do servidor (`d.perfis`), a mesma da aba Perfis.
 */
function FormDoConvite(props: {
  readonly dados: DadosDaEquipe;
  readonly aoEnviar: (aviso: string) => void;
  readonly aoCancelar: () => void;
}) {
  const d = props.dados;
  const cliente = useQueryClient();
  const [email, setEmail] = useState("");
  const [perfil, setPerfil] = useState("atendente");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const enviar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await convidarParaEquipe(cliente, email.trim(), perfil);
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível convidar.");
      return;
    }
    setEmail("");
    setPerfil("atendente");
    props.aoEnviar(r.aviso ?? "Convite enviado.");
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void enviar();
      }}
      className="hugi-equipe-form"
    >
      <label className="hugi-equipe-campo">
        <span>E-mail</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="pessoa@empresa.com.br"
          aria-label="E-mail de quem será convidado"
          data-testid="email-do-convite"
        />
        {/* Diz de onde vem a identidade, para ninguém procurar senha aqui. */}
        <small>A pessoa recebe um e-mail do Cert4All, define a própria senha e o convite vale por 72 horas.</small>
      </label>
      <label className="hugi-equipe-campo">
        <span>Entra como</span>
        <select
          value={perfil}
          onChange={(e) => setPerfil(e.target.value)}
          aria-label="Perfil de acesso de quem será convidado"
          data-testid="perfil-do-convite"
        >
          {d.perfis.map((p) => (
            <option key={p.id} value={p.id}>
              {p.rotulo}
            </option>
          ))}
        </select>
      </label>
      {erro ? <p className="hugi-equipe-erro" role="alert">{erro}</p> : null}
      <div className="hugi-equipe-acoes">
        {/* O `Botao` compartilhado é sempre `type="button"`; o Enter chega pelo `onSubmit` acima. */}
        <Botao onClick={() => void enviar()} desabilitado={ocupado} motivoDeDesabilitar="Enviando o convite…">
          {ocupado ? "Enviando…" : "Enviar convite"}
        </Botao>
        <Botao tipo="secundario" onClick={props.aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

/** Os convites que ainda não foram aceitos, com o prazo de 72h correndo. */
function Convites(props: { readonly dados: DadosDaEquipe }) {
  const d = props.dados;
  const cliente = useQueryClient();
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const operar = async (id: string, acao: "cancelar" | "reenviar") => {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    const r = await operarConviteDaEquipe(cliente, id, acao);
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível concluir.");
      return;
    }
    setAviso(r.aviso ?? "Pronto.");
  };

  return (
    <div className="hugi-equipe-convites">
      {d.convitesPendentes.length === 0 ? (
        <Vazio>Nenhum convite pendente.</Vazio>
      ) : (
        <ul>
          {d.convitesPendentes.map((c) => (
            <li key={c.id}>
              <Selo tom="neutro">convite pendente</Selo>
              <span className="hugi-equipe-corta">{c.email}</span>
              {c.expiraEm ? (
                <span className="hugi-equipe-fraco">
                  vence {new Date(c.expiraEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                </span>
              ) : null}
              <span className="hugi-equipe-links">
                <button type="button" disabled={ocupado} onClick={() => void operar(c.id, "reenviar")} className="v2-link">
                  reenviar
                </button>
                <button type="button" disabled={ocupado} onClick={() => void operar(c.id, "cancelar")} className="v2-link">
                  cancelar
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {erro ? <p className="hugi-equipe-erro" role="alert">{erro}</p> : null}
      {aviso ? <p className="hugi-equipe-fraco" role="status">{aviso}</p> : null}
    </div>
  );
}

/**
 * Os perfis — o que cada papel do Cert4All pode no Hugi. SÓ MOSTRA: o papel se administra lá, e a tela leva até lá.
 * A frase diz o que o perfil NÃO faz (ver `PERFIS` no servidor).
 */
function Perfis(props: { readonly dados: DadosDaEquipe }) {
  const [aberto, setAberto] = useState<string | null>(null);
  return (
    <div className="hugi-equipe-matriz">
      <ul>
        {props.dados.perfis.map((p) => (
          <li key={p.id}>
            <p className="hugi-equipe-perfil">
              {p.rotulo}
              {p.permissoes.includes("read_team_conversations") ? <Selo tom="info">pode ser responsável de time</Selo> : null}
            </p>
            <p className="hugi-equipe-fraco">{p.frase}</p>
            <button type="button" onClick={() => setAberto(aberto === p.id ? null : p.id)} aria-expanded={aberto === p.id} className="v2-link">
              {aberto === p.id ? "Ocultar permissões" : "Ver permissões"}
            </button>
            {aberto === p.id ? (
              <p className="hugi-equipe-permissoes">
                {p.permissoes.map((x) => (
                  <code key={x}>{x}</code>
                ))}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
      <a className="v2-botao" href={URL_DOS_PAPEIS} target="_blank" rel="noreferrer">
        <ExternalLink size={14} aria-hidden="true" />
        Administrar papéis no Cert4All
      </a>
    </div>
  );
}
